import { JsonObject, JsonProperty } from "typescript-json-serializer";
import { ExtentConfiguration } from "./geometry-configuration";
import {FieldConfiguration} from "./field-configuration";
import {PortalItemConfiguration} from "./portal-item-configuration";
import {TimeExtentConfiguration, TimeInfoConfiguration, TimeIntervalConfiguration} from "./time-info-configration";
import {SpatialReferenceConfiguration} from "./spatial-reference-configuration";
import {
    RendererConfiguration,
    SimpleRendererConfiguration,
    UniqueValueRendererConfiguration
} from "./renderer-configuration";
import {LabelClassConfiguration} from "./label-configuration";
import {FormTemplateConfiguration} from "./form-template-configuration";
import {PopupTemplateConfiguration} from "./popup-configuration";
import {GraphicConfiguration} from "./graphic-configuration";
import {BasemapConfiguration, GroundConfiguration, MapConfiguration} from "./map-configuration";

// A predicate function use to determine what is the symbol type
const rendererTypePredicate = (renderer: any) => {
    if (renderer.type === "simple") {
        return UniqueValueRendererConfiguration;
    }
    if (renderer.type === "unique-value") {
        return SimpleRendererConfiguration;
    }

    return SimpleRendererConfiguration;
};

@JsonObject()
export class LayerConfiguration {
    /**
     * The full extent of the layer.
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-Layer.html#fullExtent)
     */
    @JsonProperty({ type: ExtentConfiguration })
    declare fullExtent?: ExtentConfiguration;

    /**
     * The unique ID assigned to the layer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-Layer.html#id)
     */
    @JsonProperty()
    declare id?: string;

    /**
     * Indicates how the layer should display in the [LayerList](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList.html) widget.
     *
     * @default "show"
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-Layer.html#listMode)
     */
    @JsonProperty()
    declare listMode?: "show" | "hide" | "hide-children";

    /**
     * The opacity of the layer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-Layer.html#opacity)
     */
    @JsonProperty()
    declare opacity?: number;

    /**
     * The parent to which the layer belongs.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-Layer.html#parent)
     */
    declare parent?: MapConfiguration
    | BasemapConfiguration
    | GroundConfiguration
    | (GroupLayerConfiguration & { type: "group" })
    | (CatalogDynamicGroupLayerConfiguration & { type: "catalog-dynamic-group" })
    | (CatalogLayerConfiguration & { type: "catalog" });

    /**
     * The title of the layer used to identify it in places such as the
     * [LayerList](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList.html) widget.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-Layer.html#title)
     */
    @JsonProperty()
    declare title?: string;

    /**
     * Specifies a fixed [time extent](https://developers.arcgis.com/javascript/latest/api-reference/esri-time-TimeExtent.html) during which a layer should be visible.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-Layer.html#visibilityTimeExtent)
     */
    // visibilityTimeExtent?: TimeExtentProperties;
    /**
     * *Geo.js Property*
     * Custom field to define layer type. Reserved read-only field 'type' cannot be used
     * The layer type provides a convenient way to check the type of the layer without the need to import specific
     * layer modules.  **Possible values:** base-dynamic | base-elevation | base-tile | bing-maps | elevation |
     * feature | graphics | group | imagery | integrated-mesh | map-image | open-street-map | point-cloud | scene |
     * stream | tile | unknown | unsupported | vector-tile | web-tile
     *
     * Custom types are also possible: e.g. station
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-Layer.html#type)
     */
    @JsonProperty()
    declare layerType?: string;

    /**
     * When `true`, the layer can be persisted.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-Layer.html#persistenceEnabled)
     */
    @JsonProperty()
    declare persistenceEnabled?: boolean;

    /**
     * Indicates if the layer is visible in the [View](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-Layer.html#visible)
     */
    @JsonProperty()
    declare visible?: boolean;

    /**
     * Indicates how to manage the visibility of the children layers.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-GroupLayer.html#visibilityMode)
     */
    declare visibilityMode?: "independent" | "inherited" | "exclusive";

    /**
     * *Geo.js Property*
     * Meta ID
     *
     * @example
     */
    @JsonProperty()
    declare metauid?: string;
    /**
     * *Geo.js Property*
     * Ankerfeld für Verlinkung
     */
    @JsonProperty()
    declare anchorField?: string;

    /**
     * *Geo.js Property*
     * Ankerfeld Type für Verlinkung (z.B. integer)
     */
    @JsonProperty()
    declare anchorFieldType?: string;

    constructor() {}
}

@JsonObject()
export class PortalLayerConfiguration {
    /**
     * The portal item from which the layer is loaded.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-PortalLayer.html#portalItem)
     */
    @JsonProperty()
    declare portalItem: PortalItemConfiguration;

    constructor() {}
}
/**
 * Beinhaltet FeatureLayerBaseProperties (FeatureLayerBaseConfiguration)
 */
@JsonObject()
export class FeatureLayerConfiguration extends LayerConfiguration {
    /**
     * This property is used to configure the associated layer's [FeatureTable](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-FeatureTable.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#attributeTableTemplate)
     */
    // attributeTableTemplate?: AttributeTableTemplateProperties;
    /**
     * Array of Chart Items of type WebMapWebChart available on the feature layer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#charts)
     */
    @JsonProperty()
    declare charts?: any[];
    /**
     * Copyright information for the layer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-FeatureLayerBase.html#copyright)
     */
    @JsonProperty()
    declare copyright: string;

    /**
     * The SQL where clause used to filter features on the client.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-FeatureLayerBase.html#definitionExpression)
     */
    @JsonProperty()
    declare definitionExpression: string;

    /**
     * The name of the layer's primary display field.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-FeatureLayerBase.html#displayField)
     */
    @JsonProperty()
    declare displayField: string;

    /**
     * An object that allows you to create a dynamic layer with data either from map service sublayers or data from a registered workspace.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#dynamicDataSource)
     */
    // @JsonProperty()
    // dynamicDataSource?: (DynamicMapLayer & { type: "map-layer" }) | (DynamicDataLayer & { type: "data-layer" });
    /**
     * Determines if the layer is editable.
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#editingEnabled)
     */
    @JsonProperty()
    declare editingEnabled?: boolean;
    /**
     * Specifies how features are placed on the vertical axis (z).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-FeatureLayerBase.html#elevationInfo)
     */
    // @JsonProperty()
    // declare elevationInfo: FeatureLayerElevationInfo;

    /**
     * Configures the method for reducing the number of point features in the view.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#featureReduction)
     */
    // @JsonProperty()
    // declare featureReduction?: | (FeatureReductionBinningProperties & { type: "binning" })
    //     | (FeatureReductionClusterProperties & { type: "cluster" })
    //     | (FeatureReductionSelectionProperties & { type: "selection" });

    /**
     * An array of fields in the layer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#fields)
     */
    @JsonProperty()
    declare fields: FieldConfiguration[];
    /**
     * When a feature layer is configured as floor-aware, it has a floorInfo property defined.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-FeatureLayerBase.html#floorInfo)
     */
    // @JsonProperty()
    // declare floorInfo?: LayerFloorInfoProperties;
    /**
     * The [template](https://developers.arcgis.com/javascript/latest/api-reference/esri-form-FormTemplate.html) used in an associated layer's [FeatureForm](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-FeatureForm.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#formTemplate)
     */
    @JsonProperty()
    declare formTemplate: FormTemplateConfiguration;

    /**
     * The full extent of the layer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#fullExtent)
     */
    @JsonProperty()
    declare fullExtent?: ExtentConfiguration;

    /**
     * The version of the geodatabase of the feature service data.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-FeatureLayerBase.html#gdbVersion)
     */
    @JsonProperty()
    declare gdbVersion: string;

    /**
     * The geometry type of features in the layer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-FeatureLayerBase.html#geometryType)
     */
    @JsonProperty()
    declare geometryType?: "point" | "multipoint" | "polyline" | "polygon" | "multipatch" | "mesh";

    /**
     * Indicates whether the client-side features in the layer have `M` (measurement) values.
     *
     * @default undefined
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-FeatureLayerBase.html#hasM)
     */
    @JsonProperty()
    declare hasM: boolean;
    /**
     * Indicates whether the client-side features in the layer have `Z` (elevation) values.
     *
     * @default undefined
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-FeatureLayerBase.html#hasZ)
     */
    @JsonProperty()
    declare hasZ: boolean;

    /**
     * The historic moment to query.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-FeatureLayerBase.html#historicMoment)
     */
    @JsonProperty()
    declare historicMoment: Date;

    /**
     * Returns `true` if the layer is loaded from a non-spatial table in a service.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#isTable)
     */
    @JsonProperty()
    declare isTable: boolean;

    /**
     * The label definition for this layer, specified as an array of [LabelClass]
     * (https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-support-LabelClass.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#labelingInfo)
     */
    @JsonProperty()
    declare labelingInfo: LabelClassConfiguration[];

    /**
     * Indicates whether to display labels for this layer.
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#labelsVisible)
     */
    @JsonProperty()
    declare labelsVisible: boolean;

    /**
     * The layer ID, or layer index, of a Feature Service layer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-FeatureLayerBase.html#layerId)
     */
    @JsonProperty()
    declare layerId: number;

    /**
     * Indicates whether the layer will be included in the legend.
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#legendEnabled)
     */
    @JsonProperty()
    declare legendEnabled: boolean;

    /**
     * The name of an `oid` [field](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-FeatureLayerBase.html#fields) containing a unique value or identifier for each feature in the layer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-FeatureLayerBase.html#objectIdField)
     */
    @JsonProperty()
    declare objectIdField: string;

    /**
     * An array of field names from the service to include with each feature.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#outFields)
     */
    @JsonProperty()
    declare outFields: string[];

    /**
     * Indicates whether to display popups when features in the layer are clicked.
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#popupEnabled)
     */
    @JsonProperty()
    declare popupEnabled: boolean;

    /**
     * The popup template for the layer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#popupTemplate)
     */
    @JsonProperty()
    declare popupTemplate: PopupTemplateConfiguration;
    /**
     * Refresh interval of the layer in minutes.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#refreshInterval)
     */
    @JsonProperty()
    declare refreshInterval?: number;
    /**
     * The renderer assigned to the layer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#renderer)
     */
    @JsonProperty({type: rendererTypePredicate})
    declare renderer: RendererConfiguration;

    /**
     * When `true`, indicates that M values will be returned.
     *
     * @default undefined
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-FeatureLayerBase.html#returnM)
     */
    @JsonProperty()
    declare returnM: boolean;

    /**
     * When `true`, indicates that z-values will always be returned.
     *
     * @default undefined
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-FeatureLayerBase.html#returnZ)
     */
    @JsonProperty()
    declare returnZ: boolean;

    /**
     * Apply perspective scaling to screen-size point symbols in a [SceneView](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-SceneView.html).
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#screenSizePerspectiveEnabled)
     */
    @JsonProperty()
    declare screenSizePerspectiveEnabled: boolean;

    /**
     * The service definition expression limits the features available for display and query.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#serviceDefinitionExpression)
     */
    @JsonProperty()
    declare readonly serviceDefinitionExpression: string;

    /**
     * A collection of [Graphic](https://developers.arcgis.com/javascript/latest/api-reference/esri-Graphic.html)
     * objects used to create a FeatureLayer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#source)
     */
    @JsonProperty()
    declare source: GraphicConfiguration[];

    /**
     * The [feature service's metadata JSON](https://developers.arcgis.com/rest/services-reference/layer-feature-service-.htm) exposed by the ArcGIS REST API.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-FeatureLayerBase.html#sourceJSON)
     */
    @JsonProperty()
    declare sourceJSON: any;

    /**
     * The spatial reference of the layer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-FeatureLayerBase.html#spatialReference)
     */
    @JsonProperty()
    declare spatialReference: SpatialReferenceConfiguration;

    /**
     * The title of the layer used to identify it in places such as the [Legend](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Legend.html) and [LayerList](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-LayerList.html) widgets.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-FeatureLayerBase.html#title)
     */
    @JsonProperty()
    declare title?: string;
    /**
     * An array of feature templates defined in the feature layer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#templates)
     */
    // @JsonProperty()
    // declare templates: FeatureTemplate[];

    /**
     * The name of the field holding the type ID for the features.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#typeIdField)
     */
    @JsonProperty()
    declare typeIdField: string;

    /**
     * An array of [types](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-support-FeatureType.html) defined in the feature service exposed by ArcGIS REST API.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html#types)
     */
    // @JsonProperty()
    // declare types: FeatureTypeProperties[];

    /**
     * The absolute URL of the REST endpoint of the layer, non-spatial table or service.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-FeatureLayerBase.html#url)
     */
    @JsonProperty()
    declare url: string;

    /** --- from PortalLayer --- */
    /**
     * The portal item from which the layer is loaded.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-PortalLayer.html#portalItem)
     */
    @JsonProperty()
    declare portalItem: PortalItemConfiguration;

    /** --- end from PortalLayer --- */

    /** --- from ScaleRangeLayer --- */
    /**
     * The maximum scale (most zoomed in) at which the layer is visible in the view.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-ScaleRangeLayer.html#maxScale)
     */
    @JsonProperty()
    declare maxScale: number;
    /**
     * The minimum scale (most zoomed out) at which the layer is visible in the view.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-ScaleRangeLayer.html#minScale)
     */
    @JsonProperty()
    declare minScale: number;

    /** --- end from ScaleRangeLayer --- */

    /** --- from TemporalLayer --- */
    /**
     * The layer's time extent.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-TemporalLayer.html#timeExtent)
     */
    @JsonProperty()
    declare timeExtent?: TimeExtentConfiguration;
    /**
     * TimeInfo provides information such as date fields that store [start](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-support-TimeInfo.html#startField) and [end](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-support-TimeInfo.html#endField) time for each feature and the [fullTimeExtent](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-support-TimeInfo.html#fullTimeExtent) for the layer.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-TemporalLayer.html#timeInfo)
     */
    @JsonProperty()
    declare timeInfo?: TimeInfoConfiguration;
    /**
     * A temporary offset of the time data based on a certain [TimeInterval](https://developers.arcgis.com/javascript/latest/api-reference/esri-time-TimeInterval.html).
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-TemporalLayer.html#timeOffset)
     */
    @JsonProperty()
    declare timeOffset?: TimeIntervalConfiguration;
    /**
     * Determines if the time enabled layer will update its temporal data based on the view's [timeExtent](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html#timeExtent).
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-TemporalLayer.html#useViewTime)
     */
    @JsonProperty()
    declare useViewTime?: boolean;

    /** --- end from TemporalLayer --- */

    /** --- from OperationalLayerProperties --- */
    /**
     * Enable persistence of the layer in a [WebMap](https://developers.arcgis.com/javascript/latest/api-reference/esri-WebMap.html) or [WebScene](https://developers.arcgis.com/javascript/latest/api-reference/esri-WebScene.html).
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-OperationalLayer.html#persistenceEnabled)
     */
    @JsonProperty()
    declare persistenceEnabled?: boolean;
    /** --- end from OperationalLayerProperties --- */

    constructor() {
        super();
    }
}

@JsonObject()
export class GroupLayerConfiguration extends LayerConfiguration {
    /**
     * The maximum scale (most zoomed in) at which the layer is visible in the view.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-GroupLayer.html#maxScale)
     */
    @JsonProperty()
    declare maxScale?: number;
    /**
     * The minimum scale (most zoomed out) at which the layer is visible in the view.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-GroupLayer.html#minScale)
     */
    @JsonProperty()
    declare minScale?: number;
    /**
     * Indicates how to manage the visibility of the children layers.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-GroupLayer.html#visibilityMode)
     */
    @JsonProperty()
    declare visibilityMode?: "independent" | "inherited" | "exclusive";

    /** --- from PortalLayer --- */
    /**
     * The portal item from which the layer is loaded.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-PortalLayer.html#portalItem)
     */
    @JsonProperty()
    declare portalItem: PortalItemConfiguration;

    /** --- end from PortalLayer --- */

    /** --- from OperationalLayerProperties --- */
    /**
     * Enable persistence of the layer in a [WebMap](https://developers.arcgis.com/javascript/latest/api-reference/esri-WebMap.html) or [WebScene](https://developers.arcgis.com/javascript/latest/api-reference/esri-WebScene.html).
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-mixins-OperationalLayer.html#persistenceEnabled)
     */
    @JsonProperty()
    declare persistenceEnabled?: boolean;
    /** --- end from OperationalLayerProperties --- */
}

@JsonObject()
export class CatalogDynamicGroupLayerConfiguration extends LayerConfiguration {
    /**
     * Indicates whether the layer will be included in the legend.
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-catalog-CatalogDynamicGroupLayer.html#legendEnabled)
     */
    @JsonProperty()
    declare legendEnabled?: boolean;
    /**
     * The maximum number of sublayers that can be visible at once in a CatalogDynamicGroupLayer.
     *
     * @default 10
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-catalog-CatalogDynamicGroupLayer.html#maximumVisibleSublayers)
     */
    @JsonProperty()
    declare maximumVisibleSublayers?: number;

    constructor() {
        super();
    }
}

@JsonObject()
export class CatalogLayerConfiguration extends LayerConfiguration {
    /**
     * An array of fields in the layer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-CatalogLayer.html#fields)
     */
    @JsonProperty()
    declare fields?: FieldConfiguration[];
    /**
     * The full extent of the layer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-CatalogLayer.html#fullExtent)
     */
    @JsonProperty()
    declare fullExtent?: ExtentConfiguration;
    /**
     * Indicates whether the layer will be included in the legend.
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-CatalogLayer.html#legendEnabled)
     */
    @JsonProperty()
    declare legendEnabled?: boolean;
    /**
     * An array of field names from the service to include with each feature.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-CatalogLayer.html#outFields)
     */
    @JsonProperty()
    declare outFields?: string[];
    /**
     * The absolute URL of the REST endpoint of a catalog service.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-CatalogLayer.html#url)
     */
    @JsonProperty()
    declare url?: string;

    constructor() {
        super();
    }
}
